import { useNavigate } from "react-router-dom";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import styles from "./about.module.css";

const values = [
    {
        icon: "01",
        title: "Reliability",
        text: "We build technology that logistics teams can depend on, day in and day out, whatever the volume.",
    },
    {
        icon: "02",
        title: "Transparency",
        text: "Real-time visibility for you and your customers, from the moment a booking is made to final delivery.",
    },
    {
        icon: "03",
        title: "Ownership",
        text: "Your brand, your data, your customers. We give you the platform, you stay in control of the business.",
    },
    {
        icon: "04",
        title: "Continuous Improvement",
        text: "We ship improvements constantly, shaped directly by feedback from the operators using EFL every day.",
    },
];

const whyChooseUs = [
    {
        title: "Built specifically for logistics",
        text: "Not a generic booking tool bent into shape — every workflow is designed around pickups, drivers, warehouses, and delivery.",
    },
    {
        title: "White-labelled, end to end",
        text: "Customer site, driver app, and admin dashboard all carry your branding, not ours.",
    },
    {
        title: "Fast to launch",
        text: "Choose a template, add your details, and go live in days rather than months.",
    },
    {
        title: "Scales with your business",
        text: "From a handful of bookings a month to complex, high-volume operations, plans grow with you.",
    },
    {
        title: "Dedicated support",
        text: "A team that understands logistics operations, not just software, is on hand when you need it.",
    },
    {
        title: "Data you can trust",
        text: "Accurate, real-time reporting so decisions are based on what's actually happening on the road.",
    },
];

const AboutUs = () => {
    const navigate = useNavigate();

    useSiteBranding("About Us", null);

    return (<>
        <Header />

        <div className={`${styles.mainDiv}`}>

            <div className={`${styles.HeroSec}`}>
                <img src="/images/ef-logistics-banner-new2.png" alt="Ezy Freight Logistics driver loading a delivery van" />
                <div className={`${styles.HeroContent}`}>
                    <div className={`${styles.Container}`}>
                        <span className={`${styles.HeroTag}`}>About Ezy Freight Logistics</span>
                        <h1 className={`${styles.HeroTitle} ${styles.Heading}`}>
                            We build the platform behind your logistics business, so you can focus on running it.
                        </h1>
                        <p className={`${styles.HeroSubtitle}`}>
                            Ezy Freight Logistics gives courier and delivery businesses a complete, white-labelled
                            operating system — bookings, tracking, drivers, warehousing, and reporting, all under
                            your own brand.
                        </p>
                    </div>
                </div>
            </div>

            <div className={`${styles.StorySec}`}>
                <div className={`${styles.Container}`}>
                    <div className={`${styles.SecHeadingContainer}`}>
                        <h2 className={`${styles.BodySecHeading}`} style={{ color: "#ee233b" }}>Our Story</h2>
                    </div>
                    <div className={`${styles.StoryGrid}`}>
                        <div className={`${styles.StoryImage}`}>
                            <img src="/images/featureSec_image1.jpg" alt="Ezy Freight Logistics operations" />
                        </div>
                        <div className={`${styles.StoryText}`}>
                            <p>
                                Ezy Freight Logistics started with a simple observation: courier and delivery businesses
                                were being asked to run modern, on-demand operations on tools that were never built for
                                logistics. Spreadsheets, disconnected booking forms, and generic task trackers weren't
                                cutting it.
                            </p>
                            <p>
                                We set out to build something different — a platform that a logistics business could
                                launch under its own name, put straight in front of its customers, and hand to its
                                drivers and warehouse teams from day one, without months of custom development.
                            </p>
                            <p>
                                Today, EFL powers the full journey of a delivery: from the moment a customer books a
                                pickup, through live tracking and driver management, to warehouse scanning and the
                                reporting that keeps the whole operation accountable.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${styles.StatsSec}`}>
                <div className={`${styles.Container}`}>
                    <div className={`${styles.StatsGrid}`}>
                        <div className={`${styles.StatItem}`}>
                            <p className={`${styles.StatNumber}`}>3</p>
                            <p className={`${styles.StatLabel}`}>Ready-made templates</p>
                        </div>
                        <div className={`${styles.StatItem}`}>
                            <p className={`${styles.StatNumber}`}>24/7</p>
                            <p className={`${styles.StatLabel}`}>Platform availability target</p>
                        </div>
                        <div className={`${styles.StatItem}`}>
                            <p className={`${styles.StatNumber}`}>4</p>
                            <p className={`${styles.StatLabel}`}>Plans to fit any volume</p>
                        </div>
                        <div className={`${styles.StatItem}`}>
                            <p className={`${styles.StatNumber}`}>100%</p>
                            <p className={`${styles.StatLabel}`}>White-labelled to your brand</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${styles.ValuesSec}`}>
                <div className={`${styles.Container}`}>
                    <div className={`${styles.SecHeadingContainer}`}>
                        <h2 className={`${styles.BodySecHeading}`}>What We Stand For</h2>
                        <p className={`${styles.BodySecSubHeading}`}>Our values</p>
                    </div>
                    <div className={`${styles.ValuesGrid}`}>
                        {values.map((value) => (
                            <div className={`${styles.ValueCard}`} key={value.title}>
                                <div className={`${styles.ValueIconWrap}`}>{value.icon}</div>
                                <h3>{value.title}</h3>
                                <p>{value.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={`${styles.WhySec}`}>
                <div className={`${styles.Container}`}>
                    <div className={`${styles.SecHeadingContainer}`}>
                        <h2 className={`${styles.BodySecHeading}`} style={{ color: "#002962" }}>Why Businesses Choose EFL</h2>
                    </div>
                    <div className={`${styles.WhyGrid}`}>
                        {whyChooseUs.map((item, index) => (
                            <div className={`${styles.WhyItem}`} key={item.title}>
                                <span className={`${styles.WhyIndex}`}>{String(index + 1).padStart(2, "0")}</span>
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={`${styles.CtaSec}`}>
                <div className={`${styles.Container}`}>
                    <h2>Ready to run your own logistics platform?</h2>
                    <p>Get set up in minutes and launch under your own brand.</p>
                    <button className={`${styles.CtaButton}`} onClick={() => navigate("/sign-up")}>
                        Start Your EF Logistics Platform Today
                    </button>
                </div>
            </div>

        </div>

        <Footer />
    </>);
};

export default AboutUs;